import { useEffect, useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";

import {
  handleBlockControlKey,
  setBlockMenuOpen,
  setControlsBlock,
} from "@/components/tiptap/block-navigation/BlockNavigation";
import DefaultLineOptionsDropdown from "@/components/tiptap/utils/DefaultLineOptionsDropdown";
import { getIsTouchDevice } from "@/utils/getIsTouchDevice";
import { twMerge } from "@/utils/twMerge";

import { isCollapsed, isSectionCollapsible } from "./collapse/utils";
import { lineWrapperAt } from "./line-option-buttons/lineMenuBlocks";
import { useHoveredMenuBlock } from "./line-option-buttons/useHoveredMenuBlock";
import { useMenuLayout } from "./line-option-buttons/useMenuLayout";
import { useNavigationMenuBlock } from "./line-option-buttons/useNavigationMenuBlock";
import { getLineWrapperGetPos } from "./utils/line-wrapper/lineWrapperGetPos";
import { getLineMenu } from "./utils/LineWrapperView";

import type { DocumentModel } from "@/models/DocumentModel";
import type { KeyboardEvent } from "react";

/**
 * The block menu of the line under the pointer, or of the block whose controls have keyboard
 * focus. It floats in a portal over the editor and follows the block by its position, so it
 * survives the line's DOM being redrawn.
 */
export const LineOptionButtonsOverlay = observer(function LineOptionButtonsOverlay({
  doc,
}: {
  doc: DocumentModel;
}) {
  const { tiptap } = doc.editor;
  const t = useTranslations("tiptap");
  const root = tiptap && !tiptap.isDestroyed ? tiptap.view.dom.parentElement : null;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const navigation = useNavigationMenuBlock(tiptap, root);
  const hover = useHoveredMenuBlock(tiptap, root, isMenuOpen);
  const hoveredPos = hover.hovered?.pos ?? null;
  const pos = navigation.pos ?? hoveredPos ?? navigation.tabStopPos;
  // A read-only editor's Tab stop, which nobody points at or focuses yet, waits out of sight.
  const isIdleTabStop = pos !== navigation.pos && pos !== hoveredPos;
  const layout = useMenuLayout(tiptap, root, pos);

  const isFocusRequestMet = navigation.isFocusRequested && pos !== null && pos === navigation.pos;

  useEffect(() => {
    if (isFocusRequestMet && menuButtonRef.current) {
      menuButtonRef.current.focus();
      navigation.focusDone();
    }
  }, [isFocusRequestMet, navigation]);

  const isShown = !!tiptap && !!root && pos !== null && !!layout && !getIsTouchDevice();

  // A menu that goes away while open, as when a collaborator deletes its block, never reports
  // closing, which would leave the next one opened closed to Escape.
  useEffect(() => {
    if (!isShown && isMenuOpen) {
      setIsMenuOpen(false);

      if (tiptap) {
        setBlockMenuOpen(tiptap.view, false);
      }
    }
  }, [isShown, isMenuOpen, tiptap]);

  if (!tiptap || !root || pos === null || !layout || !isShown) {
    return null;
  }

  const node = tiptap.state.doc.nodeAt(pos);
  const lineMenu = node ? getLineMenu(tiptap, node) : undefined;
  const MenuComponent = typeof lineMenu === "function" ? lineMenu : DefaultLineOptionsDropdown;
  const isSectionCollapsedLine = isCollapsed(node) && isSectionCollapsible(node);
  const isOverToggle = pos === hover.hovered?.pos && hover.hovered.isOverToggle;
  const { line } = layout;
  // Live, unlike `pos`: an action that awaits (Cut) must find its block after collaborators' edits.
  const lineWrapper = lineWrapperAt(tiptap, pos);
  const getPos = (lineWrapper && getLineWrapperGetPos(lineWrapper)) ?? (() => pos);

  // Capture phase, before the menu button's own keys: Down would open the menu, which in a
  // read-only document moves to the next block's button instead.
  const onKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    const isOnButton =
      event.target instanceof HTMLElement && !event.target.closest('[role="menu"]');

    if (isOnButton && !isMenuOpen && handleBlockControlKey(tiptap, event.nativeEvent)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    setBlockMenuOpen(tiptap.view, open);

    if (open) {
      return;
    }

    // Headless UI returns focus to the menu button only on Escape; after an item it is left on
    // the unmounted panel, unless the item moved it (Insert Math, Ask AI).
    if (navigation.isKeyboardMenu && document.activeElement === document.body) {
      menuButtonRef.current?.focus();
    }

    if (!hover.isOverOverlay) {
      hover.clear();
    }
  };

  return createPortal(
    <>
      {/* The line's hover background, kept while the pointer is on the overlay. */}
      {!isIdleTabStop &&
        !isSectionCollapsedLine &&
        !isOverToggle &&
        line.width > 0 &&
        line.height > 0 && (
          <div
            className="pointer-events-none absolute bg-gray-50 dark:bg-gray-950 rounded-10 sm:hidden"
            style={{
              // Match `.line-wrapper:before` geometry:
              top: line.top - 5,
              left: line.left + 26,
              width: line.width + 16,
              height: line.height + 10,
              // Under line content (which is rendered with z-10).
              zIndex: 0,
            }}
          />
        )}

      <div
        style={{
          // Float above the editor, beside the block's line.
          position: "fixed",
          top: layout.top,
          left: layout.left,
          zIndex: 10,
        }}
        className={twMerge(
          "line-option-overlay pointer-events-auto sm:hidden",
          isIdleTabStop && "pointer-events-none opacity-0 focus-within:opacity-100",
        )}
        onKeyDownCapture={onKeyDownCapture}
        // A read-only editor's menu button has the controls of the block it shows, which may be
        // a hovered one rather than the block they last moved to.
        onFocus={() => {
          if (!doc.editable) {
            setControlsBlock(tiptap.view, pos);
          }
        }}
        onPointerDown={navigation.pointerUsed}
        {...hover.overlayPointerHandlers}
      >
        <MenuComponent
          doc={doc}
          getPos={getPos}
          onOpenChange={onMenuOpenChange}
          toggleButtonProps={{
            ref: menuButtonRef,
            // While editing, the menu is reached through the block controls and Tab indents; a
            // read-only editor's Tab stop is the menu, after the title's.
            tabIndex: doc.editable ? -1 : 0,
            "aria-description": doc.editable ? t("blockControlsEscapeHint") : undefined,
          }}
        />
      </div>
    </>,
    root,
  );
});
